import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  ArrowRight,
  CheckCircle2,
  Loader2,
  MessageSquare,
  Calendar,
  Send,
  Sparkles
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedBlobs,
  FloatingElement
} from '../components/AnimationComponents';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().optional(),
  company: z.string().optional(),
  service_interest: z.string().optional(),
  budget: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters')
});

// Multi-step form progress
const FormProgress = ({ step, totalSteps }) => (
  <div className="flex items-center gap-2 mb-8">
    {[...Array(totalSteps)].map((_, index) => (
      <motion.div
        key={index}
        className={`h-2 flex-1 rounded-full ${
          index < step ? 'bg-[#2563EB]' : index === step ? 'bg-[#2563EB]/50' : 'bg-slate-200'
        }`}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: index * 0.1 }}
      />
    ))}
  </div>
);

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formStep, setFormStep] = useState(0);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    trigger
  } = useForm({
    resolver: zodResolver(contactSchema)
  });

  const watchedFields = watch();

  const nextStep = async () => {
    const fieldsToValidate = formStep === 0 
      ? ['name', 'email'] 
      : formStep === 1 
      ? [] 
      : ['message'];
    
    const isValid = await trigger(fieldsToValidate);
    if (isValid) setFormStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => setFormStep(prev => Math.max(prev - 1, 0));

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, data);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <FloatingElement className="absolute top-1/4 right-[10%] hidden lg:block" duration={4} y={15}>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-white text-sm">Response Time</div>
                  <div className="text-green-400 font-bold">&lt; 24 hours</div>
                </div>
              </div>
            </div>
          </FloatingElement>
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Side - Info */}
            <div>
              <FadeInUp>
                <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Contact Us</span>
                <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                  Let's Start Your <span className="text-[#2563EB]">Transformation</span>
                </h1>
                <p className="text-white/70 text-lg mb-8">
                  Ready to take your business to the next level? Get in touch for a free consultation.
                </p>
              </FadeInUp>

              <StaggerContainer className="space-y-6" staggerDelay={0.1}>
                {[
                  { icon: Phone, label: "Phone", value: "604-267-3037", href: "tel:604-267-3037" },
                  { icon: Mail, label: "Email", value: "connect@blueribbontechnology.com", href: "mailto:connect@blueribbontechnology.com" },
                  { icon: MapPin, label: "Address", value: "1100-1200 West 73rd Avenue\nVancouver, BC, V6P 6G5", href: null },
                  { icon: Clock, label: "Hours", value: "Mon - Fri: 9AM - 6PM PST", href: null }
                ].map((item, index) => (
                  <StaggerItem key={index}>
                    <motion.div 
                      className="flex items-start gap-4 group"
                      whileHover={{ x: 5 }}
                    >
                      <motion.div 
                        className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#2563EB]/30 transition-colors"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <item.icon className="w-5 h-5 text-[#2563EB]" />
                      </motion.div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                        {item.href ? (
                          <a href={item.href} className="text-white/70 hover:text-white transition-colors whitespace-pre-line">
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-white/70 whitespace-pre-line">{item.value}</p>
                        )}
                      </div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </div>

            {/* Right Side - Form */}
            <FadeInUp delay={0.3}>
              <motion.div 
                className="bg-white p-8 rounded-3xl shadow-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {isSubmitted ? (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="contact-success"
                  >
                    <motion.div 
                      className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </motion.div>
                    <h3 className="font-outfit text-2xl font-bold text-[#0A2540] mb-4">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 mb-6">
                      We'll get back to you within 24 hours.
                    </p>
                    <Button 
                      onClick={() => { setIsSubmitted(false); setFormStep(0); }}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-[#2563EB]" />
                      <h3 className="font-outfit text-xl font-bold text-[#0A2540]">
                        Book Your Discovery Call
                      </h3>
                    </div>
                    <p className="text-slate-500 text-sm mb-6">Fill out the form and we'll be in touch shortly.</p>

                    <FormProgress step={formStep} totalSteps={3} />

                    <AnimatePresence mode="wait">
                      {/* Step 1: Basic Info */}
                      {formStep === 0 && (
                        <motion.div
                          key="step1"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                            <Input
                              {...register('name')}
                              placeholder="John Smith"
                              className="h-12 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
                              data-testid="contact-name-input"
                            />
                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                            <Input
                              {...register('email')}
                              type="email"
                              placeholder="john@company.com"
                              className="h-12 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
                              data-testid="contact-email-input"
                            />
                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                            <Input
                              {...register('phone')}
                              placeholder="604-123-4567"
                              className="h-12 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
                              data-testid="contact-phone-input"
                            />
                          </div>
                        </motion.div>
                      )}

                      {/* Step 2: Business Info */}
                      {formStep === 1 && (
                        <motion.div
                          key="step2"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                            <Input
                              {...register('company')}
                              placeholder="Your Company Inc."
                              className="h-12 rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
                              data-testid="contact-company-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Service Interest</label>
                            <Select onValueChange={(value) => setValue('service_interest', value)}>
                              <SelectTrigger className="h-12 rounded-xl" data-testid="contact-service-select">
                                <SelectValue placeholder="Select a service" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ai-solutions">AI Solutions</SelectItem>
                                <SelectItem value="erp">ERP Solutions</SelectItem>
                                <SelectItem value="digital-marketing">Digital Marketing</SelectItem>
                                <SelectItem value="consulting">Consulting</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Budget Range</label>
                            <Select onValueChange={(value) => setValue('budget', value)}>
                              <SelectTrigger className="h-12 rounded-xl" data-testid="contact-budget-select">
                                <SelectValue placeholder="Select budget" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="under-5k">Under $5,000</SelectItem>
                                <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                                <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                                <SelectItem value="50k-plus">$50,000+</SelectItem>
                                <SelectItem value="not-sure">Not sure yet</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </motion.div>
                      )}

                      {/* Step 3: Message */}
                      {formStep === 2 && (
                        <motion.div
                          key="step3"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Tell us about your project *</label>
                            <Textarea
                              {...register('message')}
                              placeholder="Describe your challenges and goals..."
                              className="min-h-[150px] rounded-xl border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]/20"
                              data-testid="contact-message-input"
                            />
                            {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    <div className="flex gap-3 mt-8">
                      {formStep > 0 && (
                        <Button
                          type="button"
                          onClick={prevStep}
                          variant="outline"
                          className="flex-1 rounded-full py-6 border-slate-200"
                        >
                          Back
                        </Button>
                      )}
                      {formStep < 2 ? (
                        <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            type="button"
                            onClick={nextStep}
                            className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full py-6"
                          >
                            Continue
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </motion.div>
                      ) : (
                        <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full py-6"
                            data-testid="contact-submit-button"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Message
                              </>
                            )}
                          </Button>
                        </motion.div>
                      )}
                    </div>

                    <p className="text-xs text-slate-500 text-center mt-4">
                      We respect your privacy. Your information will never be shared.
                    </p>
                  </form>
                )}
              </motion.div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] bg-slate-100 relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2605.5123456789!2d-123.1234567!3d49.2134567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sVancouver%2C%20BC!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          title="Blue Ribbon Technologies Location"
        />
        {/* Floating Contact Button */}
        <motion.div 
          className="absolute bottom-6 right-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <motion.a
            href="tel:604-267-3037"
            className="flex items-center gap-3 bg-[#2563EB] text-white px-6 py-4 rounded-full shadow-xl hover:bg-[#1D4ED8] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone className="w-5 h-5" />
            <span className="font-medium">Call Now</span>
          </motion.a>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              Frequently Asked Questions
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" staggerDelay={0.1}>
            {[
              { question: "What happens after I submit?", answer: "We'll reach out within 24 hours to schedule a free discovery call." },
              { question: "Is the consultation free?", answer: "Yes! The initial consultation and AI assessment are completely free." },
              { question: "How long do projects take?", answer: "Most projects take 4-8 weeks from kickoff to go-live." },
              { question: "Do you work remotely?", answer: "Yes, we work with clients across Canada and North America." }
            ].map((faq, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white p-6 rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow"
                  whileHover={{ y: -5 }}
                  data-testid={`contact-faq-${index}`}
                >
                  <h3 className="font-outfit text-lg font-semibold text-[#0A2540] mb-2">{faq.question}</h3>
                  <p className="text-slate-600 text-sm">{faq.answer}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
